<template>
  <div class="flex flex-col">
    <div v-for="section in filteredSections" :key="section.label">
      <gv-menu-divider class="my-1" />

      <div :class="padding">
        <button
          class="flex w-full flex-row items-start justify-between py-2"
          :aria-expanded="section.items.length != 0 && details[section.label]?.open"
          @click="
            () => {
              if (section.items.length != 0) details[section.label].open = !details[section.label]?.open;
              else toggleSectionCheckbox(section.label);
            }
          "
        >
          <div class="flex flex-row items-center gap-x-2">
            <gv-icon
              v-if="section.items.length != 0"
              :name="details[section.label]?.open ? 'expand_less' : 'expand_more'"
              size="xs"
              aria-hidden="true"
            />
            <gv-type
              :id="section.label.replace(/ /g, '-')"
              :text="section.label"
              uplifted-variant="heading"
              :class="section.items.length == 0 ? 'ml-6' : 'ml-0'"
              breakpoint="none"
            />
          </div>
          <!-- eslint-disable tailwindcss/no-custom-classname -->
          <input
            v-if="dropdownType == ToolbarMode.Filters && details[section.label]"
            :checked="details[section.label].selected"
            type="checkbox"
            :class="{ 'select-all-checkbox': section.items.length != 0 }"
            class="!rounded border-grey-200 !ring-0 transition"
            :aria-labelledby="section.label.replace(/ /g, '-')"
            @click.stop="toggleSectionCheckbox(section.label)"
          />
          <!-- eslint-enable tailwindcss/no-custom-classname -->
        </button>

        <!-- Items under section -->
        <div v-if="details[section.label]?.open">
          <button
            v-for="item in section.items"
            :key="item.label"
            class="flex w-full flex-row items-center justify-between py-2 pl-4"
            :class="{ '!cursor-default': !modifyingFilters }"
            @click="modifyingFilters && toggleSectionItemCheckbox(section.label, item.label)"
          >
            <div class="flex flex-row items-center gap-x-2">
              <img v-if="item.image" :src="item.image" width="24" height="24" aria-hidden="true" alt="" />
              <gv-type
                :id="item.label.replace(/ /g, '-')"
                :text="item.label"
                uplifted-variant="body-1"
                :class="{ 'pl-6': !item.image }"
                breakpoint="none"
              />
              <ui-counter v-if="item.count" :text="item.count?.toString()" />
            </div>
            <input
              v-if="modifyingFilters"
              v-model="modifyingFilters[section.label][item.label]"
              type="checkbox"
              class="!rounded border-grey-200 !ring-0 transition"
              :aria-labelledby="item.label.replace(/ /g, '-')"
            />
          </button>
        </div>
      </div>
    </div>
    <gv-menu-divider v-if="showClearFilters" class="my-1" />
    <div v-if="showClearFilters" class="flex w-full justify-end" :class="padding">
      <button @click="clearFilters()">
        <gv-type text="Clear filters" uplifted-variant="body-2" breakpoint="none" class="py-2 text-indigo-500" scheme="custom" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BaseHazardEntity } from '@/features/hazards/domain';
import { DropdownGroup, DropdownSelected, ToolbarMode, useMapStore } from '@/features/hazards/presentation/';
import { useHazardsStore } from '@/features/hazards/presentation/bloc/hazards-bloc';
import { computed, ref, watch } from 'vue';

const { hazards, typesFilters } = useHazardsStore();
const { toggleOverlay, overlayFilters } = useMapStore();

const props = withDefaults(
  defineProps<{
    filters: DropdownGroup[];
    dropdownType?: ToolbarMode;
    search: string;
    padding?: string;
    showClearFilters?: boolean;
  }>(),
  {
    dropdownType: undefined,
    search: '',
    padding: '',
    showClearFilters: true,
  }
);

const modifyingFilters = computed<DropdownSelected | undefined>(() => {
  switch (props.dropdownType) {
    case ToolbarMode.Filters:
      return typesFilters;
    case ToolbarMode.Overlays:
      return overlayFilters;
    default:
      return undefined;
  }
});

const showCount = computed<boolean>(() => props.dropdownType === ToolbarMode.Filters);

const toggleItem = computed<((section: string, item: string) => void) | undefined>(() => {
  switch (props.dropdownType) {
    case ToolbarMode.Overlays:
      return toggleOverlay;
    default:
      return undefined;
  }
});

const details = ref<Record<string, { open: boolean; selected: boolean }>>({});

watch(
  [() => props.filters, modifyingFilters],
  () => {
    details.value = Object.fromEntries(
      props.filters.map((section) => {
        const filters = Object.values(modifyingFilters.value?.[section.label] ?? {});
        if (filters.length === 0) filters.push(false);

        return [
          section.label,
          {
            /**  Default to open if there are any filters selected. This is to ensure
             * that the user can see the selected filters when they open the dropdown.
             *
             * If there are no filters selected, default to the selected state of the section.
             */
            open: !modifyingFilters.value || details.value[section.label]?.open || filters.some((s) => s),
            selected: filters.every((s) => s),
          },
        ];
      })
    );
  },
  { immediate: true, deep: true }
);

const filteredSections = computed<DropdownGroup[]>(() => {
  const searchTerm = props.search;

  // Filter the sections based on the searchTerm
  const partiallyFilteredSections = props.filters.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.label.toLowerCase().includes(searchTerm.toLowerCase())),
  }));

  const filteredSectionsWithoutCount = partiallyFilteredSections.filter(
    (section) => section.items.length != 0 || section.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!showCount.value) return filteredSectionsWithoutCount;

  // Generate count for each section's items
  const sectionsWithCount = filteredSectionsWithoutCount.map((section) => ({
    ...section,
    items: section.items.map((item) => ({
      ...item,
      count: item.hazardType
        ? (hazards[item.hazardType] as BaseHazardEntity[]).filter((record) =>
            (item.hazardSubType ?? []).find((type) => type === record.entitySubType)
          ).length
        : 0,
    })),
  }));

  return sectionsWithCount;
});

const toggleSectionCheckbox = (section: string) => {
  if (modifyingFilters.value === undefined) return;

  details.value[section].selected = !details.value[section].selected;
  const sectionFilters = modifyingFilters.value[section];

  // TODO: ensure default is set to false initially, currently just {} in the section filters.
  if (Object.keys(sectionFilters).length === 0) {
    sectionFilters['Default'] = false;
  }

  for (const item in sectionFilters) {
    if (sectionFilters[item] != details.value[section].selected) {
      if (toggleItem.value) {
        toggleItem.value(section, item);
      } else {
        sectionFilters[item] = details.value[section].selected;
      }
    }
  }
};

const toggleSectionItemCheckbox = (section: string, item: string) => {
  if (modifyingFilters.value === undefined) return;

  if (toggleItem.value) {
    toggleItem.value(section, item);
  } else {
    modifyingFilters.value[section][item] = !modifyingFilters.value[section][item];
  }

  if (modifyingFilters.value[section][item]) {
    details.value[section].selected = true;
  } else if (Object.entries(modifyingFilters.value[section]).every((detail) => !detail.at(1))) {
    details.value[section].selected = false;
  }
};

const clearFilters = () => {
  if (modifyingFilters.value === undefined) return;

  for (const section in modifyingFilters.value) {
    for (const item in modifyingFilters.value[section]) {
      if (modifyingFilters.value[section][item]) {
        if (toggleItem.value) {
          toggleItem.value(section, item);
        } else {
          modifyingFilters.value[section][item] = false;
        }
      }
    }
  }

  for (const section in details.value) {
    details.value[section].selected = false;
  }
};
</script>

<style>
.select-all-checkbox:checked {
  background-image: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24"><path fill="white" d="M240-440q-17 0-28.5-11.5T200-480q0-17 11.5-28.5T240-520h480q17 0 28.5 11.5T760-480q0 17-11.5 28.5T720-440H240Z"/></svg>');
}
</style>
